import React, { useEffect, useState } from "react";
import { api, API_ROUTES } from "../components/Api";
import { useAuthCheck } from "../components/Auth";
import Loading from "../components/Loading";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPenToSquare, faImage } from "@fortawesome/free-solid-svg-icons";

// Récupération des filtres
async function fetchProductFilters() {
    try {
        const [languagesRes, typesRes, subtypesRes] = await Promise.all([
            api.get(API_ROUTES.LANGUAGES),
            api.get(API_ROUTES.TYPES),
            api.get(API_ROUTES.SUBTYPES),
        ]);
        return {
            languages: languagesRes.data || [],
            types: typesRes.data || [],
            subtypes: subtypesRes.data || [],
        };
    } catch (err) {
        console.error("Erreur lors de la récupération des filtres:", err);
        return { languages: [], types: [], subtypes: [] };
    }
}

function AllList() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortField, setSortField] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");

    const [filters, setFilters] = useState({
        language_id: "",
        type_id: "",
        subtype_id: "",
        sold: "0",
        search: "",
    });

    const [searchInput, setSearchInput] = useState("");
    const [types, setTypes] = useState([]);
    const [subtypes, setSubtypes] = useState([]);
    const { checkToken } = useAuthCheck();

    useEffect(() => { checkToken(); }, [checkToken]);

    useEffect(() => {
        const fetchFilters = async () => {
            const data = await fetchProductFilters();
            setTypes(data.types);
            setSubtypes(data.subtypes);
        };
        fetchFilters();
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const res = await api.get(API_ROUTES.PRODUCTS, {
                    params: { page, ...filters },
                });
                setProducts(res.data.data || []);
                setTotalPages(res.data.last_page || 1);
            } catch (err) {
                console.error("Erreur lors de la récupération des produits:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [filters, page]);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const res = await api.get(API_ROUTES.PRODUCTS, {
                    params: {
                        page,
                        ...filters,
                        order_by: sortField,
                        order_dir: sortDirection
                    },
                });
                setProducts(res.data.data || []);
                setTotalPages(res.data.last_page || 1);
            } catch (err) {
                console.error("Erreur lors de la récupération des produits:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [filters, page, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const token = localStorage.getItem("token");
    if (!token) navigate("/");

    if (loading) return <Loading message="Chargement des produits..." />;

    return (
        <div className="container mx-auto p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 md:grid-cols-4 gap-4 mb-4">
                <h1 className="text-2xl font-bold dark:text-white">Liste des Produits</h1>
                <div></div>
                <div></div>
                <button
                    onClick={() => { navigate("/add-product"); }}
                    className="bg-green-500 hover:bg-green-600 text-white dark:text-black px-4 rounded"
                >
                    Ajouter un produit
                </button>
            </div>

            {/* Barre de recherche */}
            <div className="flex flex-col sm:flex-row mb-4">
                <input
                    type="text"
                    placeholder="Recherche par nom ou référence"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            setFilters({ ...filters, search: searchInput });
                            setPage(1);
                        }
                    }}
                    className="border p-2 rounded sm:rounded-r-none mb-2 sm:mb-0 flex-1 dark:bg-gray-700 bg-white"
                />
                <button
                    onClick={() => {
                        setFilters({ ...filters, search: searchInput });
                        setPage(1);
                    }}
                    className="bg-blue-500 text-white px-4 rounded sm:rounded-l-none border"
                >
                    Rechercher
                </button>
            </div>

            {/* Filtres */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <select
                    value={filters.sold}
                    onChange={(e) => setFilters({ ...filters, sold: e.target.value })}
                    className="border p-2 dark:bg-gray-700 bg-white"
                >
                    <option value="0">Non vendus</option>
                    <option value="1">Vendus</option>
                    <option value="">Tous</option>
                </select>

                <select
                    value={filters.type_id}
                    onChange={(e) => setFilters({ ...filters, type_id: e.target.value, subtype_id: "" })}
                    className="border p-2 dark:bg-gray-700 bg-white"
                >
                    <option value="">-- Type --</option>
                    {types.map((t) => (
                        <option key={t.id} value={t.id}>{t.type_name}</option>
                    ))}
                </select>

                {filters.type_id && subtypes.some(s => s.type_id === parseInt(filters.type_id)) && (
                    <select
                        value={filters.subtype_id}
                        onChange={(e) => setFilters({ ...filters, subtype_id: e.target.value })}
                        className="border p-2 dark:bg-gray-700 bg-white"
                    >
                        <option value="">-- Sous-type --</option>
                        {subtypes
                            .filter(s => s.type_id === parseInt(filters.type_id))
                            .map(s => (
                                <option key={s.id} value={s.id}>{s.subtype_name}</option>
                            ))}
                    </select>
                )}
            </div>


            {/* Tableau pour desktop */}
            <div className="hidden xl:block">
                <table className="min-w-full border">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-center">
                        <tr>
                            <th className="border px-2 py-1">Vue</th>

                            <th
                                className="border px-2 py-1 cursor-pointer select-none"
                                onClick={() => handleSort("reference")}
                            >
                                Référence{" "}
                                {sortField === "reference" && (
                                    <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                                )}
                            </th>

                            <th className="border px-2 py-1">Type</th>
                            <th className="border px-2 py-1">Sous-type</th>
                            <th className="border px-2 py-1">Image</th>
                            <th className="border px-2 py-1">Nom</th>
                            <th className="border px-2 py-1">Poids</th>
                            <th className="border px-2 py-1">Prix Achat</th>
                            <th className="border px-2 py-1">Prix Vente</th>
                            <th className="border px-2 py-1">Liens</th>

                            <th
                                className="border px-2 py-1 cursor-pointer select-none"
                                onClick={() => handleSort("created_at")}
                            >
                                Date d'ajout{" "}
                                {sortField === "created_at" && (
                                    <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                                )}
                            </th>

                            <th className="border px-2 py-1">Modifier</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((p, index) => {
                            const textColor = p.sold === 1 ? "dark:text-red-300 text-red-700" : "dark:text-white text-black";
                            const bgColor = index % 2 === 0 ? "dark:bg-gray-600 bg-gray-050" : "dark:bg-gray-700 bg-gray-100";
                            return (
                                <tr key={p.id} className={`${bgColor} ${textColor}`}>
                                    <td className="border px-2 py-1 text-center">
                                        <a href={`/product/${p.id}`} className="dark:text-blue-300 text-blue-500">
                                            <FontAwesomeIcon icon={faEye} />
                                        </a>
                                    </td>
                                    <td className="border px-2 py-1 text-center">{p.reference}

                                    </td>
                                    <td className="border px-2 py-1 text-center">{p.type?.type_name}</td>
                                    <td className="border px-2 py-1 text-center">{p.subtype?.subtype_name}</td>
                                    <td className="border px-2 py-1 text-center">
                                        {p.picture ? (
                                            <img src={`${API_ROUTES.IMG}${p.picture}`} alt={p.name} style={{ maxWidth: "128px", maxHeight: "128px" }} className="m-auto" />
                                        ) : <FontAwesomeIcon icon={faImage} />}
                                    </td>
                                    <td className="border px-2 py-1 text-center">{p.name}</td>
                                    <td className="border px-2 py-1 text-center">{p.weight}g</td>
                                    <td className="border px-2 py-1 text-center">{p.buy}€</td>
                                    <td className="border px-2 py-1 text-center">{p.sell}€</td>
                                    <td className="border px-2 py-1 text-center">
                                        {p.links && p.links.length > 0 && (
                                            <div className="space-y-2 mt-4">
                                                {p.links.map(link => (
                                                    <div>
                                                        <a
                                                            href={link.link_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="dark:text-blue-300 text-blue-500"
                                                        >
                                                            {link.type_link?.type_link_name || "Lien"}
                                                        </a>
                                                        <br />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td className="border px-2 py-1 text-center">
                                        {p.created_at ? new Date(p.created_at).toLocaleString("fr-FR") : ""}
                                    </td>
                                    <td className="border px-2 py-1 text-center">
                                        {p.sold === 0 ? (
                                            <a href={`/edit-product/${p.id}`} className="dark:text-blue-300 text-blue-500">
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                            </a>
                                        ) : (<p>Vendu</p>)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Cartes pour mobile */}
            <div className="block xl:hidden space-y-4">
                {products.map(p => (
                    <div key={p.id} className="border rounded p-2 shadow-sm grid grid-cols-3 gap-2 items-start">

                        {/* Colonne 1 : infos principales */}
                        <div className="flex flex-col space-y-1">
                            <span><strong>Nom:</strong> {p.name}</span>
                            <span><strong>Réf:</strong> {p.reference}</span>
                            <span><strong>Lot:</strong> {p.batch}</span>
                            <span><strong>Type:</strong> {p.type?.type_name}</span>
                            <span><strong>Sous-type:</strong> {p.subtype?.subtype_name}</span>
                        </div>

                        {/* Colonne 2 : infos techniques/financières */}
                        <div className="flex flex-col space-y-1">
                            <span><strong>Poids:</strong> {p.weight}g</span>
                            <span><strong>Achat:</strong> {p.buy}€</span>
                            <span><strong>Vente:</strong> {p.sell}€</span>

                            {p.sold === 0 ? (
                                <a href={`/edit-product/${p.id}`} className="dark:text-blue-300 text-blue-500 underline flex items-center space-x-1">
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                    <span>Modifier</span>
                                </a>
                            ) : (
                                <p className="font-bold text-red-500">Vendu</p>
                            )}
                        </div>

                        {/* Colonne 3 : photo + bouton vue */}
                        <div className="flex flex-col items-end space-y-2">
                            {p.picture ? (
                                <img src={`${API_ROUTES.IMG}${p.picture}`} alt={p.name} className="w-24 h-24 object-contain" />
                            ) : (
                                <FontAwesomeIcon icon={faImage} className="w-6 h-6" />
                            )}
                            <a href={`/product/${p.id}`} className="dark:text-blue-300 text-blue-500 underline flex items-center space-x-1">
                                <FontAwesomeIcon icon={faEye} />
                            </a>
                        </div>

                    </div>
                ))}
            </div>

            {/* Pagination avancée */}
            <div className="flex items-center space-x-2 mt-4 flex-wrap">
                {/* Première page */}
                <button
                    disabled={page <= 1}
                    onClick={() => setPage(1)}
                    className="border px-2 py-1 rounded dark:bg-gray-600 bg-gray-300"
                >
                    &lt;&lt;&lt;
                </button>

                {/* Page précédente */}
                <button
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    className="border px-2 py-1 rounded dark:bg-gray-500 bg-gray-200"
                >
                    &lt;
                </button>

                {/* Boutons pour pages voisines */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p =>
                        p === 1 ||
                        p === totalPages ||
                        (p >= page - 2 && p <= page + 2)
                    )
                    .map((p, idx, arr) => {
                        // Ajouter "..." si saut entre pages
                        const prev = arr[idx - 1];
                        return (
                            <React.Fragment key={p}>
                                {prev && p - prev > 1 && <span className="px-1">...</span>}
                                <button
                                    onClick={() => setPage(p)}
                                    className={`border px-2 py-1  rounded ${p === page ? "bg-blue-500 text-white dark:text-black" : "dark:bg-gray-400 bg-white"}`}
                                >
                                    {p}
                                </button>
                            </React.Fragment>
                        );
                    })}

                {/* Page suivante */}
                <button
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    className="border px-2 py-1 rounded dark:bg-gray-500 bg-gray-200"
                >
                    &gt;
                </button>

                {/* Dernière page */}
                <button
                    disabled={page >= totalPages}
                    onClick={() => setPage(totalPages)}
                    className="border px-2 py-1 rounded dark:bg-gray-600 bg-gray-300"
                >
                    &gt;&gt;&gt;
                </button>
            </div>
        </div >
    );
}

export default AllList;